// src/components/modals/PrescriptionApprovalModal.tsx
import React, { useState, useEffect, ChangeEvent } from "react";
import { Modal } from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../styles/modals.css";
import { SERVER_URL } from "../../config";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import {
  Prescription,
  Product,
  Price,
  DosageTemplate,
} from "../../types/prescription";
import { Provider } from "../../types/user";

type Pharmacy = {
  id: string;
  name: string;
  state: string;
  compound: boolean;
  shippingServiceId: number;
  shippingRecipient: string;
};

const CACHE_KEY = "pharmacies_v1";
const TTL = 24 * 60 * 60 * 1000; // 24 h

const tplDefaults = (tpl?: DosageTemplate) => ({
  quantity: tpl?.quantity ?? 1,
  refills: tpl?.refills ?? 1,
  dosage:
    tpl?.directions ??
    `${tpl?.drugStrength ?? ""} ${tpl?.drugForm ?? ""}`.trim(),
});

interface Props {
  isOpen: boolean;
  onClose: () => void;
  prescription: Prescription;
  provider: Provider;
}

const PrescriptionApprovalModal: React.FC<Props> = ({
  isOpen,
  onClose,
  prescription,
  provider,
}) => {
  const baseProd =
    (prescription as any).selectedProduct ??
    prescription.medication.products[0];
  const baseTpl: DosageTemplate | undefined = baseProd.dosage?.[0];

  const [selectedProduct, setSelectedProduct] = useState<Product>(baseProd);
  const [selectedTpl, setSelectedTpl] = useState<DosageTemplate | undefined>(
    baseTpl
  );
  const [selectedPrice, setSelectedPrice] = useState<Price>(baseProd.prices[0]);

  const [updatedRx, setUpdatedRx] = useState<Prescription>({
    ...prescription,
    ...tplDefaults(baseTpl),
  });

  const [pharmacies, setPharmacies] = useState<Pharmacy[]>([]);
  const [selectedPharmacy, setSelectedPharmacy] = useState<Pharmacy | null>(
    null
  );
  const [isApproved, setIsApproved] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const token = localStorage.getItem("token");

  /* ───── pharmacy cache / fetch ───── */
  useEffect(() => {
    if (!isOpen) return;

    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      try {
        const { ts, list } = JSON.parse(cached);
        if (Date.now() - ts < TTL) {
          setPharmacies(list);
          if (list.length === 1) setSelectedPharmacy(list[0]);
          return;
        }
      } catch {
        /* fall through */
      }
    }

    const qs = new URLSearchParams({
      state: prescription.patient.patientProfile.region || "",
      compound: String(prescription.medication.compound),
    }).toString();

    fetchWithAuth(`${SERVER_URL}/api/pharmacy?${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) =>
        r.ok ? r.json() : Promise.reject(new Error("Fetch failed"))
      )
      .then((data: Pharmacy[]) => {
        setPharmacies(data);
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({ ts: Date.now(), list: data })
        );
        if (data.length === 1) setSelectedPharmacy(data[0]);
      })
      .catch((err) => toast.error(err.message));
  }, [isOpen]);

  /* ───── handlers ───── */
  const handleField = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) =>
    setUpdatedRx((p) => ({
      ...p,
      [e.target.name]:
        e.target.type === "number"
          ? parseInt(e.target.value, 10)
          : e.target.value,
    }));

  const handlePharmacySelect = (e: ChangeEvent<HTMLSelectElement>) => {
    const pharm = pharmacies.find((p) => p.id === e.target.value) || null;
    setSelectedPharmacy(pharm);
  };

  const handleProductSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    const prod = prescription.medication.products.find(
      (p) => p.id === e.target.value
    )!;
    const tpl = prod.dosage?.[0];
    setSelectedProduct(prod);
    setSelectedTpl(tpl);
    setSelectedPrice(prod.prices[0]);
    setUpdatedRx((p) => ({
      ...p,
      ...tplDefaults(tpl),
    }));
  };

  console.log({ selectedPharmacy });

  const handleApproval = async () => {
    if (isSubmitting) return;
    if (!token) return toast.error("Login required.");
    if (!selectedPharmacy) return toast.error("Select a pharmacy");

    setIsSubmitting(true);
    try {
      const res = await fetchWithAuth(
        `${SERVER_URL}/api/prescription/${prescription.id}/approve`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            prescriptionId: updatedRx.id, // e.g. "86bfc267-…"
            patientUserId: updatedRx.patientId, // the patient’s User.id
            providerUserId: provider.userId,
            clinicId: provider.clinic.id,
            pharmacyId: selectedPharmacy.id,
            productId: selectedProduct.id, // strength row
            // planId:
            productPriceId: selectedPrice.id, //added
            stripePriceId: selectedPrice.stripePriceId,

            dosage: updatedRx.dosage, // "1mg - 8mg/mL MDV Inj Sol"
            quantity: updatedRx.quantity, // 2
            refills: updatedRx.refills, // 0
            instructions: updatedRx.instructions, // "Take once daily"

            medicationName: updatedRx.medication.name, // "Semaglutide"

            shippingServiceId: selectedPharmacy.shippingServiceId,
            shippingRecipient: selectedPharmacy.shippingRecipient,
          }),
        }
      );
      if (!res.ok) {
        const { error } = await res.json();
        throw new Error(error || "Approval failed");
      }
      toast.success("Prescription approved and dispatched.");
      onClose();
    } catch (err: any) {
      toast.error(err.message || "Approval failed");
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ───── cleanup on close ───── */
  useEffect(() => {
    if (!isOpen) {
      setIsApproved(false);
      setIsSubmitting(false);
      setSelectedPharmacy(null);
      setSelectedProduct(baseProd);
      setSelectedTpl(baseTpl);
      setSelectedPrice(baseProd.prices[0]);
      setUpdatedRx({ ...prescription, ...tplDefaults(baseTpl) });
    }
  }, [isOpen]);

  if (!isOpen) return null;
  const { patientProfile } = prescription.patient;

  /* ───── UI ───── */
  return (
    <Modal open={isOpen} onClose={onClose} aria-labelledby="prescription-modal">
      <div className="modal-overlay">
        <div className="modal-content">
          <button className="close-button" onClick={onClose}>
            &times;
          </button>

          <h2>Approve Prescription</h2>

          <p>
            <strong>Patient:</strong> {patientProfile.lastName},{" "}
            {patientProfile.firstName}
          </p>
          <p>
            <strong>Medication:</strong> {prescription.medication.name}
          </p>

          {/* ───────── dosage-template summary ───────── */}
          {selectedTpl && (
            <div className="tpl-summary">
              <p>
                <strong>{selectedTpl.pharmacyDrugName}</strong> —{" "}
                {selectedTpl.drugStrength} {selectedTpl.drugForm} ·{" "}
                {selectedTpl.quantity}
                {selectedTpl.quantityUnits} · {selectedTpl.daysSupply}-day
                supply
              </p>
            </div>
          )}

          {/* ───────── pharmacy ───────── */}
          <label className="pharmacy-select-label">
            Pharmacy
            <select
              className="pharmacy-select"
              value={selectedPharmacy?.id ?? ""}
              onChange={handlePharmacySelect}
            >
              <option value="" disabled>
                Choose…
              </option>
              {pharmacies.map((ph) => (
                <option key={ph.id} value={ph.id}>
                  {ph.name} – {ph.state}
                  {ph.compound ? " (Compound)" : ""}
                </option>
              ))}
            </select>
          </label>

          {/* ───────── strength (drugStrength/drugForm) ───────── */}
          <div className="product-select-div">
            <label className="productSelect">Select Strength:</label>
            <select
              className="product-select"
              value={selectedProduct.id}
              onChange={handleProductSelect}
            >
              {prescription.medication.products.map((prod) => {
                const tpl = prod.dosage?.[0];
                return (
                  <option key={prod.id} value={prod.id}>
                    {tpl
                      ? `${tpl.drugStrength} ${tpl.drugForm}`.trim()
                      : prod.strength}
                  </option>
                );
              })}
            </select>
          </div>

          {/* ───────── quantity / refills / dosage (all default from tpl) ───────── */}
          <div className="dosage-select-div">
            <label className="productSelect">
              Quantity ({selectedTpl?.quantityUnits ?? ""}):
            </label>
            <input
              type="number"
              name="quantity"
              value={updatedRx.quantity}
              onChange={handleField}
            />
          </div>

          <div className="dosage-select-div">
            <label className="productSelect">Refills:</label>
            <input
              type="number"
              name="refills"
              value={updatedRx.refills}
              onChange={handleField}
            />
          </div>

          <div className="dosage-select-textarea">
            <label className="productSelect">Dosage instructions:</label>
            <textarea
              name="dosage"
              value={updatedRx.dosage || ""}
              onChange={handleField}
              className="dosage-input"
            />
          </div>

          {/* ───────── action buttons ───────── */}
          {!isApproved ? (
            <>
              <button
                className="modal-button primary"
                onClick={() => setIsApproved(true)}
              >
                Approve
              </button>
              <button className="modal-button secondary" onClick={onClose}>
                Cancel
              </button>
            </>
          ) : (
            <>
              <p className="confirmation-text">
                I {provider.firstName} {provider.lastName} approve this
                prescription.
              </p>
              <button
                className="modal-button success"
                onClick={handleApproval}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting…" : "Confirm Approval"}
              </button>
              <button
                className="modal-button secondary"
                onClick={() => setIsApproved(false)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PrescriptionApprovalModal;
